import React, { useEffect, useState } from 'react';
import { X, Calendar, User, FileText } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { fetchHistory } from '../../services/api';
import { KickoffRecord } from '../../types';

export const HistoryDrawer: React.FC = () => {
  const { isHistoryOpen, setIsHistoryOpen, loadHistoricalData } = useApp();
  const [historyItems, setHistoryItems] = useState<KickoffRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isHistoryOpen) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchHistory()
      .then((records) => {
        if (isMounted) {
          setHistoryItems(records);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err?.message || 'Failed to load kickoff history.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isHistoryOpen]);

  const handleSelect = (record: KickoffRecord) => {
    loadHistoricalData(record);
    setIsHistoryOpen(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`side-panel-backdrop ${isHistoryOpen ? 'visible' : ''}`}
        onClick={() => setIsHistoryOpen(false)}
      />

      {/* Slide-out Drawer */}
      <div className={`side-panel ${isHistoryOpen ? 'open' : ''}`}>
        <div className="panel-header">
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>
            Previous Kickoffs
          </h2>
          <button
            type="button"
            className="tool-button"
            style={{ width: '32px', height: '32px' }}
            onClick={() => setIsHistoryOpen(false)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="history-list">
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
              <div className="spinner" />
            </div>
          )}

          {error && (
            <div style={{ color: '#ef4444', fontSize: '0.9rem', padding: '16px' }}>
              {error}
            </div>
          )}

          {!loading && !error && historyItems.length === 0 && (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>
              No previous extractions found.
            </p>
          )}

          {!loading &&
            !error &&
            historyItems.map((item) => {
              let dateStr = 'Unknown Date';
              if (item.date) {
                const dateObj = new Date(item.date);
                dateStr = `${dateObj.toLocaleDateString()} ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
              }

              const uploader =
                item.userName || item.userEmail || item.uploadedBy || 'Unknown User';

              return (
                <div
                  key={item.id}
                  className="history-item"
                  onClick={() => handleSelect(item)}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.8rem',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <Calendar className="h-3.5 w-3.5" />
                    <span>{dateStr}</span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: 'var(--text-main)',
                    }}
                  >
                    <FileText className="h-4 w-4 text-amber-500" />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.fileName || 'Untitled Scan'}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.8rem',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <User className="h-3.5 w-3.5" />
                    <span>Extracted by: {uploader}</span>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
};
